import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { bookingsService, type Booking } from '../../shared/services/bookings.service';
import { BookingCard } from './BookingCard';
import { BookingFilterTabs } from './BookingFilterTabs';
import { useBookingList } from './useBookingList';
import { BookingChatDrawer } from '../chat/BookingChatDrawer';

export function ProviderBookingsPage() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { filter, setFilter, bookings, isLoading, invalidate } = useBookingList('provider');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Booking | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // Counter-proposal modal state — empty until the provider clicks
  // "Propose another date" on a PENDING row.
  const [proposeTarget, setProposeTarget] = useState<Booking | null>(null);
  const [proposeDateTime, setProposeDateTime] = useState('');
  const [proposeMessage, setProposeMessage] = useState('');

  const confirm = useMutation({
    mutationFn: (id: string) => bookingsService.providerConfirm(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.confirmedSuccess'));
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      bookingsService.providerReject(id, reason),
    onMutate: (v) => setPendingId(v.id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.rejectedSuccess'));
      setRejectTarget(null);
      setRejectReason('');
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => bookingsService.providerCancel(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.cancelledSuccess'));
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const propose = useMutation({
    mutationFn: ({ id, iso, msg }: { id: string; iso: string; msg: string }) =>
      bookingsService.providerProposeNewDate(id, iso, msg || undefined),
    onMutate: (v) => setPendingId(v.id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.proposedSuccess'));
      setProposeTarget(null);
      setProposeDateTime('');
      setProposeMessage('');
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const [chatBookingId, setChatBookingId] = useState<string | null>(null);

  // Cancellation-request decisions (V13)
  const [declineCxlTarget, setDeclineCxlTarget] = useState<Booking | null>(null);
  const [declineCxlReason, setDeclineCxlReason] = useState('');

  const approveCxl = useMutation({
    mutationFn: (id: string) => bookingsService.providerApproveCancellation(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.cancellationApprovedSuccess'));
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const declineCxl = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      bookingsService.providerDeclineCancellation(id, reason),
    onMutate: (v) => setPendingId(v.id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.cancellationDeclinedSuccess'));
      setDeclineCxlTarget(null);
      setDeclineCxlReason('');
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('bookings.providerTitle')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('bookings.providerSubtitle')}
      </Typography>

      <BookingFilterTabs value={filter} onChange={setFilter} />

      {isLoading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={180} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      ) : bookings.length === 0 ? (
        <EmptyState />
      ) : (
        <Stack spacing={2}>
          {bookings.map((b) => (
            <BookingCard
              key={b.id}
              booking={b}
              perspective="provider"
              actions={providerActions(b)}
            />
          ))}
        </Stack>
      )}

      {/* Reject dialog */}
      <Dialog
        open={Boolean(rejectTarget)}
        onClose={() => !pendingId && setRejectTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('bookings.rejectDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('bookings.rejectDialogHint')}
          </Typography>
          <TextField
            label={t('bookings.rejectionReasonLabel')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 500 }}
            helperText={`${rejectReason.length}/500`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)} disabled={Boolean(pendingId)}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectTarget || Boolean(pendingId)}
            onClick={() =>
              rejectTarget && reject.mutate({ id: rejectTarget.id, reason: rejectReason.trim() })
            }
          >
            {pendingId ? t('common.loading') : t('bookings.rejectAction')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Counter-proposal dialog — provider picks a new datetime and
          optionally explains why. The customer then sees the proposed slot
          on their bookings page with [Accept] / [Refuse] buttons. */}
      <Dialog
        open={Boolean(proposeTarget)}
        onClose={() => !pendingId && setProposeTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('bookings.proposeDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('bookings.proposeDialogHint')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              type="datetime-local"
              label={t('bookings.proposeDateLabel')}
              value={proposeDateTime}
              onChange={(e) => setProposeDateTime(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                // Min = now (the backend enforces the same).
                min: toLocalInputValue(new Date().toISOString()),
              }}
            />
            <TextField
              label={t('bookings.proposeMessageLabel')}
              value={proposeMessage}
              onChange={(e) => setProposeMessage(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder={t('bookings.proposeMessagePlaceholder')}
              inputProps={{ maxLength: 500 }}
              helperText={`${proposeMessage.length}/500`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProposeTarget(null)} disabled={Boolean(pendingId)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              !proposeTarget || !proposeDateTime.trim() || Boolean(pendingId)
            }
            onClick={() => {
              if (!proposeTarget) return;
              // datetime-local gives a local-time string without tz. Convert
              // to ISO (UTC) for the backend.
              const iso = new Date(proposeDateTime).toISOString();
              propose.mutate({
                id: proposeTarget.id,
                iso,
                msg: proposeMessage.trim(),
              });
            }}
          >
            {pendingId ? t('common.loading') : t('bookings.proposeAction')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline-cancellation dialog */}
      <Dialog
        open={Boolean(declineCxlTarget)}
        onClose={() => !pendingId && setDeclineCxlTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('bookings.declineCancellationTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('bookings.declineCancellationHint')}
          </Typography>
          <TextField
            label={t('bookings.declineCancellationReasonLabel')}
            value={declineCxlReason}
            onChange={(e) => setDeclineCxlReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 500 }}
            helperText={`${declineCxlReason.length}/500`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineCxlTarget(null)} disabled={Boolean(pendingId)}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!declineCxlTarget || Boolean(pendingId)}
            onClick={() =>
              declineCxlTarget &&
              declineCxl.mutate({
                id: declineCxlTarget.id,
                reason: declineCxlReason.trim(),
              })
            }
          >
            {pendingId ? t('common.loading') : t('bookings.declineCancellationAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <BookingChatDrawer bookingId={chatBookingId} onClose={() => setChatBookingId(null)} />
    </Container>
  );

  function providerActions(b: Booking) {
    const isPending = pendingId === b.id;

    const isActive =
      b.status === 'PENDING'
      || b.status === 'CONFIRMED'
      || b.status === 'CANCELLATION_REQUESTED'
      || b.status === 'COUNTER_PROPOSED';
    const messageAction = isActive
      ? [
          {
            key: 'message',
            label: t('chat.openAction'),
            onClick: () => setChatBookingId(b.id),
            variant: 'outlined' as const,
            color: 'primary' as const,
          },
        ]
      : [];

    if (b.status === 'PENDING') {
      return [
        ...messageAction,
        {
          key: 'propose',
          label: t('bookings.proposeNewDateAction'),
          onClick: () => {
            setProposeTarget(b);
            // Pre-fill the picker with the original requested slot so the
            // provider only has to nudge the time, not re-type the whole
            // date. Trimmed to the format datetime-local expects.
            setProposeDateTime(
              b.preferredDateTime
                ? toLocalInputValue(b.preferredDateTime)
                : '',
            );
            setProposeMessage('');
          },
          variant: 'outlined' as const,
          color: 'primary' as const,
          disabled: isPending,
        },
        {
          key: 'reject',
          label: t('bookings.rejectAction'),
          onClick: () => {
            setRejectTarget(b);
            setRejectReason('');
          },
          variant: 'outlined' as const,
          color: 'error' as const,
          disabled: isPending,
        },
        {
          key: 'confirm',
          label: isPending ? t('common.loading') : t('bookings.confirmAction'),
          onClick: () => confirm.mutate(b.id),
          variant: 'contained' as const,
          color: 'success' as const,
          disabled: isPending,
        },
      ];
    }
    if (b.status === 'COUNTER_PROPOSED') {
      // Provider already counter-proposed — they wait for the customer.
      // Only messaging stays available (no Cancel/Withdraw here for now;
      // a future iteration can add a "Withdraw proposal" action).
      return messageAction;
    }
    if (b.status === 'CANCELLATION_REQUESTED') {
      return [
        ...messageAction,
        {
          key: 'decline-cancellation',
          label: t('bookings.declineCancellationAction'),
          onClick: () => {
            setDeclineCxlTarget(b);
            setDeclineCxlReason('');
          },
          variant: 'outlined' as const,
          color: 'error' as const,
          disabled: isPending,
        },
        {
          key: 'approve-cancellation',
          label: isPending ? t('common.loading') : t('bookings.approveCancellationAction'),
          onClick: () => approveCxl.mutate(b.id),
          variant: 'contained' as const,
          color: 'success' as const,
          disabled: isPending,
        },
      ];
    }
    if (b.status === 'CONFIRMED') {
      return [
        ...messageAction,
        {
          key: 'cancel',
          label: isPending ? t('common.loading') : t('bookings.cancelAction'),
          onClick: () => cancel.mutate(b.id),
          variant: 'outlined' as const,
          color: 'error' as const,
          disabled: isPending,
        },
      ];
    }
    return messageAction;
  }
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 6, md: 10 },
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 4,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
          color: 'primary.main',
        }}
      >
        <InboxRoundedIcon sx={{ fontSize: 36 }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t('bookings.providerEmptyTitle')}
      </Typography>
      <Typography color="text.secondary">{t('bookings.providerEmptyHint')}</Typography>
    </Box>
  );
}

/** Convert an ISO-8601 string (or browser-native ISO from `new Date().toISOString()`)
 *  to the `YYYY-MM-DDTHH:mm` format the `<input type="datetime-local">`
 *  expects in its `value` and `min` attributes. The conversion goes through
 *  the **local** timezone so the displayed time matches what the user picks. */
function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getFullYear() +
    '-' + pad(d.getMonth() + 1) +
    '-' + pad(d.getDate()) +
    'T' + pad(d.getHours()) +
    ':' + pad(d.getMinutes())
  );
}
